import { ROUTES } from '../route-configuration';

export interface PageConfig {
  title: string;
  description?: string;
  headerButtons?: HeaderButton[];
  showSidebar?: boolean;
  showIcons?: boolean;
  layout?: 'standard' | 'editor';
  sidebarMode?: 'normal' | 'locked-collapsed';
}

export interface HeaderButton {
  id: string;
  label: string;
  type: 'button' | 'link' | 'dropdown';
  variant?: 'primary' | 'secondary';
  onClick?: () => void;
  href?: string;
  icon?: string;
  dropdownItems?: DropdownItem[];
}

export interface DropdownItem {
  id: string;
  label: string;
  icon?: string;
  onClick?: () => void;
  href?: string;
  divider?: boolean;
}

// Page-specific configurations
export const PAGE_CONFIGS: Record<string, PageConfig> = {
  [ROUTES.DASHBOARD]: {
    title: 'Dashboard',
    headerButtons: [
      {
        id: 'create-proposal',
        label: 'Create proposal',
        type: 'button',
        variant: 'primary',
      },
    ],
    showSidebar: true,
    showIcons: true,
  },
  [ROUTES.CLIENTS]: {
    title: 'Clients',
    headerButtons: [
      {
        id: 'create-client',
        label: 'Create client',
        type: 'button',
        variant: 'primary',
      },
    ],
    showSidebar: true,
    showIcons: true,
  },
  [ROUTES.CLIENT_DETAIL]: {
    title: 'Client',
    description: 'View client details and proposals',
    showSidebar: true,
    showIcons: true,
  },
  [ROUTES.PROPOSAL_EDIT]: {
    title: 'Proposal editor',
    description: 'Edit your proposal and generate a first draft with AI',
    showSidebar: false,
    showIcons: true,
    layout: 'editor',
    sidebarMode: 'locked-collapsed',
  },
  [ROUTES.PROPOSAL_PREVIEW]: {
    title: 'Proposal preview',
    description: 'Preview your proposal as it will appear to recipients',
    showSidebar: false,
    showIcons: true,
    layout: 'editor',
    sidebarMode: 'locked-collapsed',
  },
  [ROUTES.PROPOSAL_TIMELINE]: {
    title: 'Proposal timeline',
    showSidebar: true,
    showIcons: true,
  },
  [ROUTES.PROPOSALS_INDEX]: {
    title: 'Proposals',
    description: 'All proposals in your workspace',
    showSidebar: true,
    showIcons: true,
  },
  [ROUTES.PROPOSALS_DRAFTS]: {
    title: 'Draft proposals',
    description: 'All proposals currently in draft',
    showSidebar: true,
    showIcons: true,
  },
  [ROUTES.PROPOSALS_SENT]: {
    title: 'Sent proposals',
    description: 'Proposals that have been shared with clients',
    showSidebar: true,
    showIcons: true,
  },
  [ROUTES.PROPOSALS_SIGNED]: {
    title: 'Signed proposals',
    description: 'Finalized proposals that have been signed',
    showSidebar: true,
    showIcons: true,
  },
  [ROUTES.TEMPLATES]: {
    title: 'Templates',
    description: 'Create and manage proposal templates',
    headerButtons: [
      {
        id: 'create-template',
        label: 'Create Template',
        type: 'button',
        variant: 'primary',
        icon: 'plus',
        onClick: () => console.log('Create template clicked'),
      },
      {
        id: 'template-gallery',
        label: 'Browse Gallery',
        type: 'link',
        variant: 'secondary',
        icon: 'grid',
        href: '/templates/gallery',
      },
    ],
    showSidebar: true,
    showIcons: true,
  },
  [ROUTES.ACCOUNT_USERS]: {
    title: 'Users',
    description: 'Manage who has access to your workspace',
    showSidebar: true,
    showIcons: true,
  },
  [ROUTES.ACCOUNT_SETTINGS]: {
    title: 'Account settings',
    description: 'Manage your account details and business profile',
    showSidebar: true,
    showIcons: true,
  },

  [ROUTES.HOME]: {
    title: 'Proposio',
    description: 'AI-powered proposal generation',
    showSidebar: false,
  },
  [ROUTES.LOGIN]: {
    title: 'Sign In',
    showSidebar: false,
  },
  [ROUTES.SIGNUP]: {
    title: 'Sign Up',
    showSidebar: false,
  },
  [ROUTES.FORGOT_PASSWORD]: {
    title: 'Password Reset',
    showSidebar: false,
  },
};

const matchRoutePattern = (pattern: string, pathname: string): boolean => {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = pathname.split('/').filter(Boolean);

  if (patternParts.length !== pathParts.length) return false;

  return patternParts.every((part, index) => {
    const segment = pathParts[index] ?? '';
    if (part.startsWith(':')) {
      return segment.length > 0;
    }
    return part === segment;
  });
};

// Helper function to get page config
const normalizePageConfig = (config: PageConfig): PageConfig => {
  return {
    layout: 'standard',
    sidebarMode: 'normal',
    ...config,
  };
};

export const getPageConfig = (pathname: string): PageConfig => {
  const directMatch = PAGE_CONFIGS[pathname];
  if (directMatch) return normalizePageConfig(directMatch);

  const dynamicMatch = Object.keys(PAGE_CONFIGS).find(
    (route) => route.includes(':') && matchRoutePattern(route, pathname)
  );
  if (dynamicMatch) return normalizePageConfig(PAGE_CONFIGS[dynamicMatch]);

  return normalizePageConfig({
    title: 'Page',
    showSidebar: true,
  });
};
